import type { ReactNode } from "react";
import type { CandidateStep } from "@talenthop/shared";
import { useTheme } from "../../lib/theme.js";
import { Check, Moon, ShieldCheck } from "../../components/icons.js";

/**
 * Theme 1 chrome shared by the public candidate pages (invite journey, find-my-link, speech
 * check). Purely presentational — no data, no behaviour beyond the theme toggle.
 */

/** Forest square with a lime shield — the TalentHop mark on public pages. */
export function BrandMark({ size = 30 }: { size?: number }): ReactNode {
  return (
    <span className="t1c-mark" style={{ width: size, height: size }} aria-hidden="true">
      <ShieldCheck />
    </span>
  );
}

/** Top bar: brand mark + wordmark, an optional right-hand slot, and the theme toggle. */
export function CandidateTopBar({ label, right }: { label: string; right?: ReactNode }): ReactNode {
  const { cycle } = useTheme();
  return (
    <div className="cx-bar t1c-bar">
      <BrandMark />
      <span className="t1c-word">
        <b>TalentHop</b>
        <span className="t1c-word-sub">{label}</span>
      </span>
      <span className="t1c-bar-right">
        {right}
        <button className="theme-toggle" onClick={cycle} aria-label="Toggle theme" data-tip="Light / dark theme" data-tip-pos="left">
          <Moon />
        </button>
      </span>
    </div>
  );
}

/**
 * The ONE candidate-facing stepper (audit 15 / top-15 #15). Five stages that map 1:1 onto the
 * server-tracked steps the candidate actually acts on; "done" is not a stage, it is the end state
 * (every segment complete). The caption and the segments are derived from the same index, so the
 * "Step 3/4" vs 6-dot contradiction cannot come back.
 */
export const STAGES: { key: Exclude<CandidateStep, "done">; label: string }[] = [
  { key: "invite", label: "Your details" },
  { key: "consent", label: "Consent" },
  { key: "schedule", label: "Schedule" },
  { key: "checkin", label: "Check-in" },
  { key: "interview", label: "Interview" },
];

export function CandidateStepper({ step, note }: { step: CandidateStep; note?: string }): ReactNode {
  const idx = step === "done" ? STAGES.length : STAGES.findIndex((s) => s.key === step);
  const current = STAGES[idx];
  return (
    <div className="t1c-stepper" aria-label="Interview progress">
      <div className="t1c-stepper-cap">
        {current ? (
          <>
            <b>
              Step {idx + 1} of {STAGES.length}
            </b>{" "}
            · {current.label}
            {note ? <span className="t1c-stepper-note"> · {note}</span> : null}
          </>
        ) : (
          <b>All steps complete</b>
        )}
      </div>
      <ol className="t1c-segs">
        {STAGES.map((s, i) => {
          const state = i < idx ? "done" : i === idx ? "now" : "todo";
          return (
            <li key={s.key} className={`t1c-seg ${state}`} aria-current={state === "now" ? "step" : undefined}>
              <span className="t1c-seg-bar" />
              <span className="t1c-seg-label">
                {state === "done" && <Check />}
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
